import { addMilliseconds, addMinutes, isAfter, isBefore, parseISO } from 'date-fns'
import { RRule, Weekday } from 'rrule'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  buildDefaultEventTitle,
  isOOOEvent,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { BOOKING_CHANNEL_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/types'
import { CLIENT_SELF_PAY_STATUS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  getTimeZoneName,
  getTimezoneOffset,
  toUTCISOString,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { isIntakeSession } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  BuiltEvent,
  EventExtendedProps,
  EventModality,
  isBendAppointmentType,
  isSameBookingType,
  isSameModality,
  LyraEventExtendedPropsNoTZ,
  ProviderCalendarEvent,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { APPOINTMENT_STATUS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { employmentTypes, healthPlanStates } from '../common/constants/appConstants'
import { isHPIAndSelfPaySupported } from '../common/utils/utils'
import { AppDispatch } from '../data/store'
import { getCustomerProgramCoverageBreakdown } from '../requestPayment/data/requestPaymentActions'

export function mapEventToBookableEvent(
  event: BuiltEvent,
  timeZone: string,
  recurring: boolean = true,
  hybridBookingMode: boolean = false,
) {
  let recurrence = undefined
  if (recurring) {
    const weekday = addMilliseconds(parseISO(event.startTime), getTimezoneOffset(timeZone, event.startTime)).getUTCDay()
    recurrence = [
      new RRule({
        freq: RRule.WEEKLY,
        byweekday: [new Weekday(weekday === 0 ? 6 : weekday - 1)],
      }).toString(),
    ]
  }
  return {
    title: event.title ?? buildDefaultEventTitle(hybridBookingMode),
    start: event.startTime,
    end: event.endTime,
    timezone: timeZone,
    event_type: recurring ? 'bookable_recurring' : 'bookable',
    recurrence,
    modality_availability: event.modality,
    booking_type: event.booking_type,
  }
}

export function mapExtendedProps(
  event: ProviderCalendarEvent,
  appointments: { [key: number]: Appointment },
  calendarName: string,
  externalIdAppointmentIdMap: { [key: string]: Appointment },
  showModalitySection?: boolean,
): EventExtendedProps {
  const eventType = event.extended_properties?.private?.lyraEventType
  const modality = event.extended_properties?.private?.modality_availability
  const hasConferenceData = event.has_conference_data
  const isOOO = isOOOEvent(event.title)
  const isPrivate = event.event_type === 'private'
  const bendAppointmentType = event.extended_properties?.private?.bendAppointmentType

  const untypedAppointmentId = event.extended_properties?.private?.appointmentId
  const appointmentId =
    untypedAppointmentId && typeof untypedAppointmentId === 'string' ? parseInt(untypedAppointmentId) : null
  const clientTimeZone = getTimeZoneFromExtendedProperties(event)

  // To minimize changes to provider types without Modality selection, breakout mapping modality to a unique case
  if ((eventType === 'bookable_recurring' || eventType === 'bookable') && showModalitySection) {
    return {
      lyraEventType: eventType,
      externalId: event.external_id,
      modality_availability: modality ? (modality as EventModality) : undefined,
      booking_type: event.booking_type,
    }
  }

  if (eventType === 'bookable_recurring' || eventType === 'bookable') {
    return {
      lyraEventType: eventType,
      externalId: event.external_id,
      booking_type: event.booking_type,
    }
  }

  if (eventType === 'session' && appointmentId && appointmentId in appointments) {
    const appointment = appointments[appointmentId]
    return {
      ...getSessionExtendedProperties(appointment),
      clientTimeZone,
    }
  }

  const bendAppointmentId = event.extended_properties?.private?.bendAppointmentId
  if (isBendAppointmentType(bendAppointmentType) && typeof bendAppointmentId === 'string' && bendAppointmentId) {
    return {
      lyraEventType: 'session',
      bendAppointmentType,
      externalId: event.external_id,
      clientTimeZone,
      description: event.description,
      calendarConfigurationId: event.provider_calendar_configuration_id,
      calendarName,
      isPrivateEvent: isPrivate,
      bendAppointmentId,
    }
  }

  // It is possible for an event to not have an appointmentId set in the extended properties due to other services
  // such as SimplePractice modifiying the event. In this case, we can try to find correct appointment by matching the eventId
  if (event.external_id in externalIdAppointmentIdMap) {
    return {
      ...getSessionExtendedProperties(externalIdAppointmentIdMap[event.external_id]),
      clientTimeZone,
    }
  }

  return {
    lyraEventType: 'generic',
    description: event.description,
    externalId: event.external_id,
    calendarConfigurationId: event.provider_calendar_configuration_id,
    hasConferenceData,
    clientTimeZone,
    lyraAppointmentId: appointmentId,
    isOOOEvent: isOOO,
    isPrivateEvent: isPrivate,
    calendarName,
  }
}

const isValidIANATimeZone = (tz: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

const getTimeZoneFromExtendedProperties = (event: ProviderCalendarEvent): string | null => {
  const newTimeZone = event.extended_properties?.private?.clientCityTimeZone
  if (newTimeZone && typeof newTimeZone === 'string' && isValidIANATimeZone(newTimeZone)) {
    return newTimeZone
  }

  const oldTimeZone = event.extended_properties?.private?.clientTimeZone
  if (oldTimeZone && typeof oldTimeZone === 'string' && isValidIANATimeZone(oldTimeZone)) {
    return oldTimeZone
  }
  return null
}

export const getSessionExtendedProperties = (appointment: Appointment): LyraEventExtendedPropsNoTZ => {
  const isClientInitiatedLate = appointment.changeReason === 'clientInitiatedLate' && !appointment.isChargeReversed

  const isCoveredLateCancel = isClientInitiatedLate && appointment.infractionStatus === 'covered'
  const isClientOwedLateCancel = isClientInitiatedLate && appointment.infractionStatus === 'client_owed'

  return {
    lyraEventType: 'session',
    lyraProgramId: appointment.programId,
    appointmentClass: isIntakeSession(appointment) ? 'initial' : 'recurring',
    appointmentStatus: appointment.appointmentStatus,
    appointmentType: appointment.appointmentType,
    sessionType: appointment.sessionType,
    lyraAppointmentId: appointment.appointmentId,
    clientFirstName: appointment.userInfo?.firstName,
    clientLastName: appointment.userInfo?.lastName,
    clientPhoneNumber: appointment.userInfo?.phone,
    clientEmail: appointment.userInfo?.username,
    appointmentSessionNumber: appointment.sessionNumber,
    appointmentMeetingFormat: appointment.meetingFormat,
    appointmentAddress: appointment.address,
    appointmentVideoPlatform: appointment.videoPlatform,
    clientId: appointment.userInfo?.lyraId ?? null,
    isNoShow: appointment.appointmentStatus === 'missed',
    appointmentBookedDetails: undefined, // this is populated when the event popover is opened
    treatmentType: appointment.treatmentType,
    isChargeReversed: appointment.isChargeReversed,
    isCoveredLateCancel,
    isClientOwedLateCancel,
    guardianLyraId: appointment.guardianLyraId,
    guardianEmail: appointment.guardianEmail,
    guardianFirstName: appointment.guardianFirstName,
    guardianLastName: appointment.guardianLastName,
    guardianPhone: appointment.guardianPhone,
    childFirstName: appointment.childFirstName,
    providerScheduled:
      appointment.providerScheduled || appointment.bookingChannel === BOOKING_CHANNEL_TYPE.PROVIDER_INITIATED_BOOKING,
  }
}

/**
 * Creates a synthetic calendar event from a cancelled appointment.
 * This is used to display late-cancelled sessions in the IPC when the
 * Calendar Service event has been deleted but the appointment data still exists.
 */
export const createSyntheticEventFromCancelledAppointment = (
  appointment: Appointment,
  timeZone: string,
): {
  id: string
  title: string
  start: string
  end: string
  startEditable: boolean
  extendedProps: EventExtendedProps
} => {
  const startUTC = getDateFromAppointment(appointment)
  const endUTC = addMinutes(startUTC, appointment.appointmentDuration)

  return {
    id: `cancelled-${appointment.appointmentId}`,
    title: appointment.title,
    start: toUTCISOString(startUTC.toISOString(), timeZone),
    end: toUTCISOString(endUTC.toISOString(), timeZone),
    startEditable: false,
    extendedProps: {
      clientTimeZone: appointment.timeZone ? (getTimeZoneName({ timeZone: appointment.timeZone }) ?? null) : null,
      ...getSessionExtendedProperties(appointment),
    },
  }
}

/**
 * Filters appointments to only include late-cancelled ones within a given date range.
 * Late cancellations are identified by changeReason === 'clientInitiatedLate'
 * and an infractionStatus of 'covered' or 'client_owed'.
 */
export const filterLateCancelledAppointments = (
  appointments: Appointment[] | undefined,
  startDate: string,
  endDate: string,
): Appointment[] => {
  const rangeStart = parseISO(startDate)
  const rangeEnd = parseISO(endDate)
  return (
    appointments?.filter((appt) => {
      if (appt.appointmentStatus !== APPOINTMENT_STATUS.CANCELED) {
        return false
      }
      if (appt.changeReason !== 'clientInitiatedLate') {
        return false
      }
      if (appt.infractionStatus !== 'covered' && appt.infractionStatus !== 'client_owed') {
        return false
      }
      const apptStart = getDateFromAppointment(appt)
      return isAfter(apptStart, rangeStart) && isBefore(apptStart, rangeEnd)
    }) ?? []
  )
}

/**
 * Returns true if a calendar event (availability block) overlaps with any
 * of the provided late-cancelled appointments.
 */
export const eventOverlapsWithLateCancellations = (
  event: ProviderCalendarEvent,
  lateCancelledAppointments: Appointment[],
): boolean => {
  if (event.event_type !== 'bookable') {
    return false
  }
  const eventStart = parseISO(event.start_datetimetz)
  const eventEnd = parseISO(event.end_datetimetz)
  return lateCancelledAppointments.some((appt) => {
    const apptStart = getDateFromAppointment(appt)
    const apptEnd = addMinutes(apptStart, appt.appointmentDuration)
    return apptStart < eventEnd && apptEnd > eventStart
  })
}

export const getBookableTarget = (employmentStatus?: string): number => {
  switch (employmentStatus) {
    case employmentTypes.FULL_TIME:
      return 30
    case employmentTypes.PART_TIME:
      return 19
    case employmentTypes.REDUCED_FULL_TIME:
      return 24
    case employmentTypes.CLINICAL_QUALITY_COORDINATOR:
      return 15
    case employmentTypes.FELLOW_SUPERVISOR:
      return 24
    case employmentTypes.TEST:
      return 3
    default:
      return 30
  }
}

export const getWorkingHours = (employmentStatus?: string): number => {
  switch (employmentStatus) {
    case employmentTypes.FULL_TIME:
      return 40
    case employmentTypes.PART_TIME:
      return 24
    case employmentTypes.FULL_TIME_REDUCED:
    case employmentTypes.REDUCED_FULL_TIME:
      return 32
    case employmentTypes.CLINICAL_QUALITY_CONSULTANTS:
    case employmentTypes.CLINICAL_QUALITY_COORDINATOR:
      return 40
    case employmentTypes.FELLOW_SUPERVISOR:
      return 32
    case employmentTypes.TEST:
      return 5
    default:
      return 40
  }
}

export const getBookableMaxSlots = (employmentStatus?: string): number => {
  switch (employmentStatus) {
    case employmentTypes.FULL_TIME:
      return 36
    case employmentTypes.PART_TIME:
      return 21
    case employmentTypes.REDUCED_FULL_TIME:
      return 29
    case employmentTypes.CLINICAL_QUALITY_COORDINATOR:
      return 18
    case employmentTypes.FELLOW_SUPERVISOR:
      return 29
    case employmentTypes.TEST:
      return 5
    default:
      return 36
  }
}

type SlotsToUpdate = {
  slotsToAdd: { startTime: string; endTime: string; modality?: EventModality | null; booking_type?: string[] | null }[]
  slotsToRemove: {
    startTime: string
    endTime: string
    id: string
    modality?: EventModality | null
    booking_type?: string[] | null
  }[]
}

const getLocalTimeString = (time: string, timeZone: string) => {
  const eventStart = parseISO(time)
  return eventStart.toLocaleDateString('en-US', { weekday: 'long', hour: '2-digit', minute: '2-digit', timeZone })
}
/**
 * Resolves the HPI/self-pay banner variant to render, and whether the client's coverage for this
 * program is HPI-only (no EAP).
 */
export const getHPISelfPayBannerVariantForClient = async ({
  client,
  programName,
  isSelfPayEnabled,
}: {
  client: ClientObject | undefined
  programName: string | undefined
  isSelfPayEnabled: boolean | undefined
  dispatch: AppDispatch
}): Promise<{ variant: 'paymentMissing' | 'needsInfoOrEnrollment' | undefined; isHpiOnlyCoverage: boolean }> => {
  const none = { variant: undefined, isHpiOnlyCoverage: false } as const
  if (!isSelfPayEnabled || !client?.customer_support_self_pay || !client?.employer) {
    return none
  }
  try {
    const breakdownAction = getCustomerProgramCoverageBreakdown(client.employer, client.country)
    const breakdownResponse = await breakdownAction.payload
    const programCoverageBreakdown = breakdownResponse?.data
    const isHpiOnlyCoverage = isHPIAndSelfPaySupported({
      clientDetails: client,
      programName,
      programCoverageBreakdown,
      isSelfPayEnabled,
    })
    if (!isHpiOnlyCoverage) {
      return none
    }
    const healthPlanStatus = client.health_plan_status
    const isSelfPayActive = client.self_pay_status === CLIENT_SELF_PAY_STATUS.ACTIVE
    const isHealthPlanIneligibleOrMissing =
      healthPlanStatus === healthPlanStates.NOT_READY || healthPlanStatus === healthPlanStates.NOT_ELIGIBLE
    if (healthPlanStatus === healthPlanStates.HEALTH_PLAN_PAYMENT_MISSING) {
      return { variant: 'paymentMissing', isHpiOnlyCoverage }
    }
    if (isHealthPlanIneligibleOrMissing && !isSelfPayActive) {
      return { variant: 'needsInfoOrEnrollment', isHpiOnlyCoverage }
    }
    return { variant: undefined, isHpiOnlyCoverage }
  } catch {
    return { variant: undefined, isHpiOnlyCoverage: true }
  }
}

export const getSlotsToUpdate = (
  newSlots: {
    startTime: string
    endTime: string
    modality?: EventModality | null
    title?: string | null
    booking_type?: string[] | null
  }[],
  oldSlots: {
    startTime: string
    endTime: string
    id: string
    modality?: EventModality | null
    title?: string | null
    booking_type?: string[] | null
  }[],
  timeZone: string,
): SlotsToUpdate => {
  const slotsToRemove = oldSlots.filter(
    (oldSlot) =>
      !newSlots.find(
        (newSlot) =>
          getLocalTimeString(newSlot.startTime, timeZone) === getLocalTimeString(oldSlot.startTime, timeZone) &&
          getLocalTimeString(newSlot.endTime, timeZone) === getLocalTimeString(oldSlot.endTime, timeZone) &&
          isSameModality(newSlot.modality, oldSlot.modality) &&
          isSameBookingType(newSlot.booking_type, oldSlot.booking_type),
      ),
  )

  const slotsToAdd = newSlots.filter(
    (newSlot) =>
      !oldSlots.find(
        (oldSlot) =>
          getLocalTimeString(newSlot.startTime, timeZone) === getLocalTimeString(oldSlot.startTime, timeZone) &&
          getLocalTimeString(newSlot.endTime, timeZone) === getLocalTimeString(oldSlot.endTime, timeZone) &&
          isSameModality(newSlot.modality, oldSlot.modality) &&
          isSameBookingType(newSlot.booking_type, oldSlot.booking_type),
      ),
  )
  return {
    slotsToAdd,
    slotsToRemove,
  }
}
